import type { TestCase } from '@k-ucap/shared';
import type { TestCaseRepository } from './RepositoryInterfaces.js';

export class InMemoryTestCaseRepository implements TestCaseRepository {
  private cases: Map<string, TestCase> = new Map();

  async findAll(): Promise<TestCase[]> {
    return Array.from(this.cases.values()).sort((a, b) => a.executionOrder - b.executionOrder);
  }

  async findById(id: string): Promise<TestCase | undefined> {
    return this.cases.get(id);
  }

  async findBySuiteId(suiteId: string): Promise<TestCase[]> {
    return Array.from(this.cases.values())
      .filter((c) => c.suiteId === suiteId)
      .sort((a, b) => a.executionOrder - b.executionOrder);
  }

  async findByProjectId(projectId: string): Promise<TestCase[]> {
    return Array.from(this.cases.values())
      .filter((c) => c.projectId === projectId)
      .sort((a, b) => a.executionOrder - b.executionOrder);
  }

  async save(testCase: TestCase): Promise<TestCase> {
    this.cases.set(testCase.id, testCase);
    return testCase;
  }

  async delete(id: string): Promise<void> {
    this.cases.delete(id);
  }

  async duplicate(id: string, newName?: string): Promise<TestCase | undefined> {
    const original = this.cases.get(id);
    if (!original) return undefined;
    const duplicated: TestCase = {
      ...original,
      id: `tc-${Date.now()}`,
      name: newName || `${original.name} (Copy)`,
      executionOrder: original.executionOrder + 1,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    };
    this.cases.set(duplicated.id, duplicated);
    return duplicated;
  }

  seed(cases: TestCase[]): void {
    cases.forEach((c) => this.cases.set(c.id, c));
  }
}

export const createInMemoryTestCaseRepository = (): InMemoryTestCaseRepository => new InMemoryTestCaseRepository();
